// IE Hub - Group Field Component
// Array/repeater field for dynamic field groups

import React, { forwardRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  FormHelperText,
  IconButton,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

interface GroupField {
  name: string;
  label?: string;
  defaultValue?: unknown;
  [key: string]: unknown;
}

interface GroupValidation {
  required?: boolean;
}

interface GroupProps {
  label?: string;
  value?: Array<Record<string, unknown>>;
  fields?: GroupField[];
  minItems?: number;
  maxItems?: number;
  variant?: 'card' | 'accordion' | 'simple';
  sortable?: boolean;
  collapsible?: boolean;
  addButtonText?: string;
  removeButtonText?: string;
  validation?: GroupValidation;
  onValidate?: (isValid: boolean, error: string) => void;
  renderField?: (field: GroupField, value: unknown, onChange: (value: unknown) => void) => React.ReactNode;
  sx?: Record<string, unknown>;
  error?: boolean;
  helperText?: string;
  onChange?: (event: { target: { value: Array<Record<string, unknown>> } }) => void;
}

const Group = forwardRef<HTMLDivElement, GroupProps>(({
  label,
  value = [],
  fields = [],
  minItems = 0,
  maxItems = 10,
  variant = 'card',
  sortable = false,
  collapsible = false,
  addButtonText = 'Add Item',
  removeButtonText = 'Remove',
  validation = {},
  onValidate,
  renderField,
  sx = {},
  ...props
}, ref) => {
  const [validationError, setValidationError] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  
  // Validation logic
  const validateInput = (items: Array<Record<string, unknown>>) => {
    let error = '';
    
    // Required validation
    if (validation.required && (!items || items.length === 0)) {
      error = 'At least one item is required';
    }
    
    // Min items validation
    if (items && items.length < minItems) {
      error = `At least ${minItems} items are required`;
    }
    
    // Max items validation
    if (items && items.length > maxItems) {
      error = `No more than ${maxItems} items are allowed`;
    }
    
    setValidationError(error);
    
    if (onValidate) {
      onValidate(error === '', error);
    }
    
    return error === '';
  };
  
  // Handle adding a new item
  const handleAddItem = () => {
    if (value.length >= maxItems) return;
    
    // Create new item with default values
    const newItem: Record<string, unknown> = {};
    fields.forEach((field: GroupField) => {
      newItem[field.name] = field.defaultValue ?? '';
    });
    
    const newValue = [...value, newItem];
    
    if (Object.keys(validation).length > 0) {
      validateInput(newValue);
    }
    
    // Auto-expand new item if collapsible
    if (collapsible) {
      setExpandedItems(prev => new Set([...prev, newValue.length - 1]));
    }
    
    if (props.onChange) {
      props.onChange({
        target: { value: newValue }
      });
    }
  };
  
  // Handle removing an item
  const handleRemoveItem = (index: number) => {
    if (value.length <= minItems) return;
    
    const newValue = value.filter((_item, i: number) => i !== index);
    
    if (Object.keys(validation).length > 0) {
      validateInput(newValue);
    }
    
    // Update expanded items indices
    if (collapsible) {
      const newExpanded = new Set<number>();
      expandedItems.forEach((expandedIndex: number) => {
        if (expandedIndex < index) {
          newExpanded.add(expandedIndex);
        } else if (expandedIndex > index) {
          newExpanded.add(expandedIndex - 1);
        }
      });
      setExpandedItems(newExpanded);
    }
    
    if (props.onChange) {
      props.onChange({
        target: { value: newValue }
      });
    }
  };
  
  // Handle field change within an item
  const handleFieldChange = (itemIndex: number, fieldName: string, fieldValue: unknown) => {
    const newValue = [...value];
    newValue[itemIndex] = {
      ...newValue[itemIndex],
      [fieldName]: fieldValue
    };
    
    if (props.onChange) {
      props.onChange({
        target: { value: newValue }
      });
    }
  };
  
  // Handle accordion expand/collapse
  const handleAccordionChange = (index: number) => (_event: unknown, isExpanded: boolean) => {
    const newExpanded = new Set(expandedItems);
    if (isExpanded) {
      newExpanded.add(index);
    } else {
      newExpanded.delete(index);
    }
    setExpandedItems(newExpanded);
  };
  
  // Render individual field
  const renderFieldComponent = (field: GroupField, value: unknown, onChange: (value: unknown) => void) => {
    if (renderField) {
      return renderField(field, value, onChange);
    }
    
    // Default field rendering - this would integrate with the field registry
    return (
      <div key={field.name}>
        {/* Placeholder for field component */}
        <Typography variant="caption">{field.label || field.name}</Typography>
        <input
          type="text"
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%', padding: 8, margin: '4px 0' }}
        />
      </div>
    );
  };
  
  // Render item based on variant
  const renderItem = (item: Record<string, unknown>, index: number) => {
    const itemKey = `item-${index}`;
    
    const itemContent = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map((field: GroupField) => renderFieldComponent(
          field,
          item[field.name] as unknown,
          (value: unknown) => handleFieldChange(index, field.name, value)
        ))}
      </Box>
    );
    
    const itemActions = (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        {sortable && (
          <IconButton size="small" sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon />
          </IconButton>
        )}
        <IconButton
          onClick={() => handleRemoveItem(index)}
          disabled={value.length <= minItems}
          color="error"
          size="small"
          title={removeButtonText}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
    
    if (variant === 'accordion') {
      return (
        <Accordion
          key={itemKey}
          expanded={expandedItems.has(index)}
          onChange={handleAccordionChange(index)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {label} {index + 1}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {itemContent}
            {itemActions}
          </AccordionDetails>
        </Accordion>
      );
    }
    
    if (variant === 'card') {
      return (
        <Card key={itemKey} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">
                {label} {index + 1}
              </Typography>
              {itemActions}
            </Box>
            {itemContent}
          </CardContent>
        </Card>
      );
    }
    
    // Simple variant
    return (
      <Box key={itemKey} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2">
            {label} {index + 1}
          </Typography>
          {itemActions}
        </Box>
        {itemContent}
      </Box>
    );
  };
  
  return (
    <Box ref={ref} sx={{ marginBottom: 2, ...sx }}>
      {label && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
          {label}
          {validation.required && (
            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
          )}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {value.map((item, index) => renderItem(item, index))}
      </Box>
      
      {value.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            color: 'text.secondary'
          }}
        >
          <Typography variant="body2">
            No items added yet
          </Typography>
        </Box>
      )}
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          disabled={value.length >= maxItems}
          variant="outlined"
          size="small"
        >
          {addButtonText}
        </Button>
        
        <Typography variant="caption" color="text.secondary">
          {value.length} of {maxItems} items
        </Typography>
      </Box>
      
      {validationError && (
        <FormHelperText error sx={{ mt: 1 }}>
          {validationError}
        </FormHelperText>
      )}
    </Box>
  );
});

Group.displayName = 'Group';

export default Group;
